import React, { useMemo, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { tenantApi } from '../../services/api';
import ExecutiveSummaryCard from '../../components/msp/ExecutiveSummaryCard';
import PrioritizedFixList from '../../components/msp/PrioritizedFixList';
import TenantExecutiveFindingDetails from '../../components/findings/TenantExecutiveFindingDetails';
import './Msp.css';

function TenantReportDetail() {
  const { tenantId, reportId } = useParams();
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState('loading');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [exportTheme, setExportTheme] = useState('default');
  const [approveStatus, setApproveStatus] = useState('idle');
  const [exportStatus, setExportStatus] = useState('idle');

  useEffect(() => {
    let isMounted = true;

    tenantApi
      .listReports(tenantId)
      .then((response) => {
        if (!isMounted) return;
        const reports = Array.isArray(response.data?.reports) ? response.data.reports : response.data;
        const match = Array.isArray(reports)
          ? reports.find((item) => String(item.id || item.report_id) === String(reportId))
          : response.data;
        setReport(match);
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [tenantId, reportId]);

  const severityData = useMemo(() => {
    if (!report?.findings?.length) return [];
    const counts = report.findings.reduce((acc, finding) => {
      const key = finding.severity || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [report]);

  const riskTrend = useMemo(() => {
    const trend = report?.risk_trend || report?.riskTrend || [];
    if (!Array.isArray(trend)) return [];
    return trend.map((point, index) => ({
      label: point?.label || `T-${trend.length - index}`,
      value: typeof point === 'number' ? point : point?.value || point?.risk_score || 0,
    }));
  }, [report]);

  const handleApproveReport = async () => {
    setApproveStatus('submitting');
    try {
      await tenantApi.approveReport(tenantId, reportId);
      setApproveStatus('success');
    } catch (error) {
      console.error('Unable to approve report', error);
      setApproveStatus('error');
    }
  };

  const handleExport = async () => {
    setExportStatus('submitting');
    try {
      await tenantApi.exportReport(tenantId, reportId, exportFormat);
      setExportStatus('success');
    } catch (error) {
      console.error('Unable to export report', error);
      setExportStatus('error');
    }
  };

  const handleDownloadPenTestReport = async () => {
    if (!report?.latest_external_scan_id) return;
    setExportStatus('submitting');
    try {
      const response = await tenantApi.exportExternalScanReport(
        tenantId,
        report.latest_external_scan_id,
        'pdf'
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Tenant_${tenantId}_External_Attack_Surface_Report_${report.latest_external_scan_id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setExportStatus('success');
    } catch (error) {
      console.error('Unable to download pen-test report', error);
      setExportStatus('error');
    }
  };

  return (
    <div className="msp-page">
      <div className="msp-header">
        <div>
          <h1>Report Detail</h1>
          <p className="msp-subtitle">Tenant {tenantId}</p>
        </div>
        <Link className="msp-pill" to={`/msp/tenants/${tenantId}/reports`}>
          Back to Reports
        </Link>
      </div>

      {status === 'loading' && <div className="msp-empty">Loading report…</div>}
      {status === 'error' && (
        <div className="msp-empty">Unable to load report details.</div>
      )}
      {status === 'ready' && !report && (
        <div className="msp-empty">Report details not found.</div>
      )}
      {status === 'ready' && report && (
        <>
          <div className="msp-grid">
            <div className="msp-card">
              <span className="msp-card-label">Report</span>
              <span className="msp-card-title">{report.title || report.name || reportId}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Status</span>
              <span className="msp-card-value">{report.status || 'In Progress'}</span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Generated</span>
              <span className="msp-card-value">
                {report.generated_at || report.created_at || 'Pending'}
              </span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Download</span>
              <span className="msp-card-value">
                {report.download_url ? (
                  <a className="msp-link" href={report.download_url}>
                    PDF Report
                  </a>
                ) : (
                  'Pending'
                )}
              </span>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Shareable Link</span>
              <span className="msp-card-value">
                {report.share_url || report.shareUrl || report.share_link ? (
                  <a
                    className="msp-link"
                    href={report.share_url || report.shareUrl || report.share_link}
                  >
                    Copy Link
                  </a>
                ) : (
                  'Not shared'
                )}
              </span>
            </div>
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Report Actions</div>
            <div className="msp-action-stack">
              <div className="msp-action-row">
                <select
                  className="msp-input"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value)}
                >
                  <option value="pdf">Export PDF</option>
                  <option value="csv">Export CSV</option>
                </select>
                <select
                  className="msp-input"
                  value={exportTheme}
                  onChange={(event) => setExportTheme(event.target.value)}
                >
                  <option value="default">Default Theme</option>
                  <option value="tenant">Tenant Theme</option>
                  <option value="partner">Partner Theme</option>
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleExport}
                  disabled={exportStatus === 'submitting'}
                >
                  {exportStatus === 'submitting' ? 'Exporting…' : 'Export'}
                </button>
              </div>
              {report.latest_external_scan_id && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDownloadPenTestReport}
                  disabled={exportStatus === 'submitting'}
                >
                  {exportStatus === 'submitting'
                    ? 'Preparing External Report…'
                    : 'Download External Attack Surface Report'}
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApproveReport}
                disabled={approveStatus === 'submitting'}
              >
                {approveStatus === 'submitting' ? 'Approving…' : 'Approve Report'}
              </button>
              {approveStatus === 'error' && <div className="msp-empty">Unable to approve report.</div>}
            </div>
          </div>

          <div className="msp-grid">
            <div className="msp-card">
              <span className="msp-card-label">Risk Score Trend</span>
              <div className="msp-chart-card">
                {riskTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={riskTrend}>
                      <defs>
                        <linearGradient id="reportTrend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00f3ff" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#00f3ff" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" stroke="#8a94a6" />
                      <YAxis stroke="#8a94a6" />
                      <CartesianGrid stroke="rgba(255,255,255,0.08)" />
                      <Tooltip />
                      <Area type="monotone" dataKey="value" stroke="#00f3ff" fill="url(#reportTrend)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="msp-empty">No trend data.</div>
                )}
              </div>
            </div>
            <div className="msp-card">
              <span className="msp-card-label">Severity Distribution</span>
              <div className="msp-chart-card">
                {severityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Tooltip />
                      <Pie data={severityData} dataKey="value" nameKey="name" fill="#00f3ff" />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="msp-empty">No findings data.</div>
                )}
              </div>
            </div>
          </div>

          <div className="msp-grid">
            <ExecutiveSummaryCard
              summary={report.executive_summary || report.summary}
              changes={report.change_summary}
              nextSteps={report.next_steps}
            />
            <PrioritizedFixList items={report.top_priorities || report.priorities} />
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Findings Snapshot</div>
            {report.findings?.length ? (
              <table className="msp-table">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Finding</th>
                    <th>Severity</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.findings.map((finding) => (
                    <tr key={finding.id || finding.finding_id}>
                      <td>{finding.priorityRank || finding.priority_rank || '—'}</td>
                      <td>
                        <div>{finding.title || finding.name}</div>
                        <TenantExecutiveFindingDetails finding={finding} />
                      </td>
                      <td>{finding.severity}</td>
                      <td>{finding.status || 'Open'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="msp-empty">No findings in this report.</div>
            )}
          </div>

          <div className="msp-section">
            <div className="msp-section-title">Client Feedback Log</div>
            {(report.comments || report.comment_log || []).length ? (
              <div className="msp-timeline">
                {(report.comments || report.comment_log || []).map((comment) => (
                  <div key={comment.id || comment.timestamp} className="msp-timeline-item">
                    <div className="msp-timeline-title">{comment.author || comment.user || 'Client'}</div>
                    <div className="msp-timeline-meta">
                      <span>{comment.timestamp || comment.created_at || 'Recent'}</span>
                      <span>{comment.message || comment.note || comment.comment}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="msp-empty">No client feedback submitted.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TenantReportDetail;
